import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Fab,
  Button,
  Stack,
  Autocomplete,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { BottomSheet, BottomSheetActions } from './BottomSheet';
import { HelpTooltip } from '../Onboarding';
import { format } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { MobileStorePicker, type MobileStore } from './MobileStorePicker';
import { useStoreManagement } from '../../context/StoreManagementContext';
import { useStoresQuery } from '../../hooks/queries';
import {
  getStoreItemAvailability,
  createItemBlackout,
  deleteItemBlackout,
} from '../../api';
import {
  searchProducts,
  searchBundles,
  searchVariants,
  searchModifiers,
  searchOptionValues,
} from '../../api/products';
import type {
  ProductSearchResult,
  BundleSearchResult,
  VariantSearchResult,
  ModifierSearchResult,
  OptionValueSearchResult,
} from '../../api/products';
import {
  BlackoutItem,
  BlackoutType,
  BlackoutReason,
  BlackoutTypeLabels,
  BlackoutReasonLabels,
  getBlackoutType,
  getItemCode,
  getItemDisplayName,
} from '../ItemAvailability/types';

interface SelectedItem {
  type: BlackoutType;
  code: string;
  name: string;
}

export const MobileSnoozeItems: React.FC = () => {
  const { data: stores = [], isLoading: storesLoading } = useStoresQuery();
  const { selectedMobileStore, setSelectedMobileStore } = useStoreManagement();
  const selectedStoreNumber = selectedMobileStore;
  const setSelectedStoreNumber = setSelectedMobileStore;
  const [blackouts, setBlackouts] = useState<BlackoutItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBlackout, setSelectedBlackout] = useState<BlackoutItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [itemType, setItemType] = useState<BlackoutType>('PRODUCT');
  const [reason, setReason] = useState<BlackoutReason>('OUT_OF_STOCK');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<(ProductSearchResult | BundleSearchResult | VariantSearchResult | ModifierSearchResult | OptionValueSearchResult)[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [expiry, setExpiry] = useState<Date | null>(getDefault4AM());
  const [selectedQuickOption, setSelectedQuickOption] = useState<string>('endOfDay');

  // Filter state
  const [filterQuery, setFilterQuery] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      resetForm();
    }
  }, [dialogOpen]);

  // Convert stores to mobile format
  const mobileStores: MobileStore[] = stores.map(s => ({
    storeNumber: s.storeNumber,
    name: s.name || s.storeNumber,
    isActive: s.isActive,
  }));

  // Auto-select first store if only one
  useEffect(() => {
    if (mobileStores.length === 1 && !selectedStoreNumber) {
      setSelectedStoreNumber(mobileStores[0].storeNumber);
    }
  }, [mobileStores, selectedStoreNumber]);

  // Fetch blackouts when store changes
  useEffect(() => {
    if (selectedStoreNumber) {
      fetchBlackouts();
    }
  }, [selectedStoreNumber]);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, itemType]);

  function getDefault4AM() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(4, 0, 0, 0);
    return tomorrow;
  }

  const fetchBlackouts = async () => {
    if (!selectedStoreNumber) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getStoreItemAvailability(selectedStoreNumber, 100);
      setBlackouts(response?.blackouts || []);
    } catch (err) {
      console.error('Failed to fetch snoozed items:', err);
      setError('Failed to load snoozed items');
      setBlackouts([]);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (query: string) => {
    setIsSearching(true);
    try {
      let results: (ProductSearchResult | BundleSearchResult | VariantSearchResult | ModifierSearchResult | OptionValueSearchResult)[] = [];
      switch (itemType) {
        case 'PRODUCT':
          results = await searchProducts(query);
          break;
        case 'BUNDLE':
          results = await searchBundles(query);
          break;
        case 'VARIANT':
          results = await searchVariants(query);
          break;
        case 'MODIFIER':
          results = await searchModifiers(query);
          break;
        case 'OPTION_VALUE':
          results = await searchOptionValues(query);
          break;
      }
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCreateSnooze = async () => {
    if (!selectedStoreNumber || selectedItems.length === 0 || !expiry) return;

    setSubmitting(true);
    setError(null);

    try {
      const productCodes = selectedItems.filter(i => i.type === 'PRODUCT').map(i => i.code);
      const bundleCodes = selectedItems.filter(i => i.type === 'BUNDLE').map(i => i.code);
      const variantCodes = selectedItems.filter(i => i.type === 'VARIANT').map(i => i.code);
      const modifierCodes = selectedItems.filter(i => i.type === 'MODIFIER').map(i => i.code);
      const optionValueCodes = selectedItems.filter(i => i.type === 'OPTION_VALUE').map(i => i.code);

      await createItemBlackout(
        [selectedStoreNumber],
        productCodes,
        bundleCodes,
        reason,
        expiry.toISOString(),
        new Date().toISOString(),
        variantCodes,
        modifierCodes,
        optionValueCodes,
        selectedItems // Pass items with names for audit
      );

      setDialogOpen(false);
      resetForm();
      setSuccess(`${selectedItems.length} item(s) snoozed successfully`);
      setTimeout(() => setSuccess(null), 3000);
      fetchBlackouts();
    } catch (err) {
      console.error('Failed to snooze item:', err);
      setError('Failed to snooze item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnsnooze = async () => {
    if (!selectedStoreNumber || !selectedBlackout) return;

    setSubmitting(true);
    setError(null);

    try {
      const type = getBlackoutType(selectedBlackout);
      const code = getItemCode(selectedBlackout);

      await deleteItemBlackout(selectedStoreNumber, type, code);

      setDeleteDialogOpen(false);
      setSelectedBlackout(null);
      setSuccess('Item is now available');
      setTimeout(() => setSuccess(null), 3000);
      fetchBlackouts();
    } catch (err: any) {
      console.error('Failed to activate item:', err);
      setError(err.response?.data?.message || 'Failed to activate item');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItemType('PRODUCT');
    setReason('OUT_OF_STOCK');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedItems([]);
    setExpiry(getDefault4AM());
    setSelectedQuickOption('endOfDay');
  };

  const getTimeRemaining = (expiryDate: string) => {
    const now = new Date();
    const exp = new Date(expiryDate);
    const diff = exp.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d remaining`;
    }

    return `${hours}h ${minutes}m`;
  };

  const filteredBlackouts = blackouts.filter(b => {
    if (!filterQuery) return true;
    const searchTerm = filterQuery.toLowerCase();
    const code = getItemCode(b).toLowerCase();
    const name = (b.name || '').toLowerCase();
    return code.includes(searchTerm) || name.includes(searchTerm);
  });

  const quickExpiryOptions = [
    { label: '1H', key: '1h', getValue: () => { const d = new Date(); d.setHours(d.getHours() + 1); return d; } },
    { label: '2H', key: '2h', getValue: () => { const d = new Date(); d.setHours(d.getHours() + 2); return d; } },
    { label: '4H', key: '4h', getValue: () => { const d = new Date(); d.setHours(d.getHours() + 4); return d; } },
    { label: 'EOD', key: 'endOfDay', getValue: getDefault4AM },
    { label: '1W', key: '1week', getValue: () => { const d = new Date(); d.setDate(d.getDate() + 7); return d; } },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      {/* Store Picker */}
      <MobileStorePicker
        stores={mobileStores}
        selectedStore={selectedStoreNumber}
        onStoreChange={setSelectedStoreNumber}
        loading={storesLoading}
      />

      {/* Search/Filter */}
      {selectedStoreNumber && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8f9fa' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter snoozed items..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.light',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 4 }}>
        {!selectedStoreNumber ? (
          // Empty state - welcome banner in store picker handles this
          null
        ) : loading || storesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredBlackouts.length === 0 ? (
          <Box 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <Typography color="text.secondary">
              {filterQuery ? 'No matching items' : 'No items are currently snoozed'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Tap + to snooze an item
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            {/* Header with count */}
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  Snoozed Items
                </Typography>
                <HelpTooltip title="Items temporarily removed from the menu. Tap the checkmark to make an item available again." iconSize={14} />
              </Box>
              <Chip
                size="small"
                label={filteredBlackouts.length}
                sx={{ 
                  height: 18, 
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'warning.50',
                  color: 'warning.dark',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>
            <List disablePadding dense>
              {filteredBlackouts.map((blackout, index) => {
                const type = getBlackoutType(blackout);
                const isExpired = new Date(blackout.expiry) < new Date();

                return (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        py: 1,
                        px: 2,
                        minHeight: 56,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1, minWidth: 0 }}>
                              {getItemDisplayName(blackout)}
                            </Typography>
                            <Chip
                              label={BlackoutTypeLabels[type]}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                            <Chip
                              label={BlackoutReasonLabels[blackout.reason]}
                              size="small"
                              color={blackout.reason === 'OUT_OF_STOCK' ? 'error' : 'warning'}
                              sx={{ height: 16, fontSize: '0.55rem' }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <TimeIcon sx={{ fontSize: 12, color: isExpired ? 'error.main' : 'text.disabled' }} />
                              <Typography variant="caption" color={isExpired ? 'error.main' : 'text.secondary'} sx={{ fontSize: '0.65rem' }}>
                                {getTimeRemaining(blackout.expiry)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            setSelectedBlackout(blackout);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ 
                            color: 'success.main',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < filteredBlackouts.length - 1 && <Divider sx={{ ml: 2, mr: 6 }} />}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}
        
        {/* Bottom spacer */}
        <Box sx={{ height: 80 }} />
      </Box>

      {/* FAB to add */}
      {selectedStoreNumber && (
        <Fab
          color="primary"
          onClick={() => setDialogOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 32px)',
            right: 16,
            boxShadow: '0 4px 12px rgba(102, 44, 139, 0.3)',
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Snooze Item Dialog */}
      <BottomSheet
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); resetForm(); }}
        title="Snooze Item"
        subtitle="Temporarily disable items from ordering"
        maxHeight="90vh"
        actions={
          <BottomSheetActions
            onCancel={() => { setDialogOpen(false); resetForm(); }}
            onConfirm={handleCreateSnooze}
            confirmText={`Snooze ${selectedItems.length > 0 ? `(${selectedItems.length})` : ''}`}
            confirmDisabled={submitting || selectedItems.length === 0 || !expiry}
            confirmLoading={submitting}
          />
        }
      >
        <Stack spacing={2}>
          {/* Item Type Chips */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
              ITEM TYPE
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {[
                { value: 'PRODUCT', label: 'Product' },
                { value: 'BUNDLE', label: 'Bundle' },
                { value: 'VARIANT', label: 'Variant' },
                { value: 'MODIFIER', label: 'Modifier' },
                { value: 'OPTION_VALUE', label: 'Option' },
              ].map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  variant={itemType === opt.value ? 'filled' : 'outlined'}
                  color={itemType === opt.value ? 'primary' : 'default'}
                  onClick={() => {
                    setItemType(opt.value as BlackoutType);
                    setSearchQuery('');
                    setSearchResults([]);
                    setSelectedItems([]);
                  }}
                  sx={{ fontWeight: itemType === opt.value ? 600 : 400 }}
                />
              ))}
            </Stack>
          </Box>

          {/* Search */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
              SEARCH & SELECT ITEMS
            </Typography>
            <Autocomplete
              value={null}
              onChange={(_, newValue) => {
                if (newValue && !selectedItems.some(i => i.code === newValue.code)) {
                  setSelectedItems([...selectedItems, {
                    type: itemType,
                    code: newValue.code,
                    name: newValue.name,
                  }]);
                }
                setSearchQuery('');
                setSearchResults([]);
              }}
              inputValue={searchQuery}
              onInputChange={(_, value) => setSearchQuery(value)}
              options={searchResults}
              getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
              loading={isSearching}
              loadingText="Searching..."
              noOptionsText={searchQuery.length < 2 ? 'Type to search...' : 'No results'}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter code or name..."
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: 2 },
                    endAdornment: (
                      <>
                        {isSearching && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 2, border: 1, borderColor: 'primary.100' }}>
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {selectedItems.map((item) => (
                  <Chip
                    key={item.code}
                    label={item.name}
                    size="small"
                    onDelete={() => setSelectedItems(selectedItems.filter(i => i.code !== item.code))}
                    sx={{ maxWidth: 150 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Reason */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
              REASON
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {Object.entries(BlackoutReasonLabels).map(([value, label]) => (
                <Chip
                  key={value}
                  label={label}
                  size="small"
                  variant={reason === value ? 'filled' : 'outlined'}
                  color={reason === value ? 'secondary' : 'default'}
                  onClick={() => setReason(value as BlackoutReason)}
                />
              ))}
            </Stack>
          </Box>

          {/* Duration */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                DURATION
              </Typography>
              <HelpTooltip title="Item automatically returns to menu after this time. EOD = End of Day (4 AM next day)." iconSize={14} />
            </Box>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
              {quickExpiryOptions.map((opt) => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  size="small"
                  variant={selectedQuickOption === opt.key ? 'filled' : 'outlined'}
                  color={selectedQuickOption === opt.key ? 'primary' : 'default'}
                  onClick={() => {
                    setExpiry(opt.getValue());
                    setSelectedQuickOption(opt.key);
                  }}
                />
              ))}
            </Stack>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDateTimePicker
                label="Custom time"
                value={expiry}
                onChange={(val) => {
                  setExpiry(val);
                  setSelectedQuickOption('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    helperText: expiry ? `Reactivates: ${format(expiry, 'MMM d, h:mm a')}` : null,
                  },
                }}
                minDateTime={new Date()}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </BottomSheet>

      {/* Make Available Dialog */}
      <BottomSheet
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Make Item Available"
        maxHeight="50vh"
        actions={
          <BottomSheetActions
            onCancel={() => setDeleteDialogOpen(false)}
            onConfirm={handleUnsnooze}
            cancelText="Keep Snoozed"
            confirmText="Make Available"
            confirmColor="success"
            confirmDisabled={submitting}
            confirmLoading={submitting}
            confirmIcon={<CheckIcon />}
          />
        }
      >
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CheckIcon sx={{ color: 'success.main', fontSize: 28 }} />
          </Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This item will become available for ordering immediately.
          </Typography>
          {selectedBlackout && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {getItemDisplayName(selectedBlackout)}
              </Typography>
            </Box>
          )}
        </Box>
      </BottomSheet>
    </Box>
  );
};
